import { computed } from 'vue'
import { FormatNumberUnitType } from '@/shared/types/units.type'
import { usePreferredLanguages } from '@vueuse/core'
import { useStoreUser } from '@/stores/user'
import { convertUnit } from '@/shared/utils/ConvertUnits'

export const useFormatNumber = () => {
  const storeUser = useStoreUser()

  const language = usePreferredLanguages()
  const maximumFractionDigits = computed(() => storeUser.stateSettings.number_decimals_max)

  // Quantified value with symbol as a string
  const withQuantifiedSymbol = (
    num: number = 0,
    decimals: number = 2,
    unit?: FormatNumberUnitType,
    symbols?: string[] | any
  ): string => {
    let formattingNumber = num
    let formattingUnit: string | undefined = unit
    let supported = !!formattingUnit

    switch (unit) {
      case 'gram':
        // if (num > 10 ** 6) {
        //   formattingUnit = 't'
        //   formattingNumber = num / 10 ** 6
        //   supported = false
        // }
        if (num > 10 ** 3) {
          formattingUnit = 'kilogram'
          formattingNumber = num / 10 ** 3
        } else {
          formattingNumber = num
        }
        break

      case 'meter':
        if (num > 10 ** 6) {
          formattingUnit = 'm'
          formattingNumber = num / 10 ** 6
          supported = false
        } else if (num > 10 ** 3) {
          formattingUnit = 'km'
          formattingNumber = num / 10 ** 3
        } else {
          formattingNumber = num
        }
        break

      case 'gram-per-minute':
        formattingNumber = num
        break

      case 'mg/min':
        if (num > 10 ** 6) {
          formattingNumber = num / 10 ** 6
          formattingUnit = 'kg/min'
        } else if (num > 10 ** 3) {
          formattingNumber = num / 10 ** 3
          formattingUnit = 'g/min'
        } else {
          formattingNumber = num
        }
        supported = false
        break

      case 'kg/m3':
        supported = false
        break

      case 'liter-per-minute':
        formattingNumber = num
        break
    }
    if (!supported) {
      return (
        formatNumber(formattingNumber, { maximumFractionDigits: decimals }).value +
        (formattingUnit ? ' ' + formattingUnit : '')
      )
    }

    return formatNumber(formattingNumber, { style: 'unit', unit: formattingUnit, maximumFractionDigits: decimals })
      .value
  }

  const withUnitSeparate = (
    value: number,
    decimals?: number,
    unit?: string,
    toUnit?: string
  ): { value: string; unit: string | undefined } => {
    const { value: formattingNumber, unit: formattingUnit } = convertUnit(value, unit, toUnit)

    // Make units more readable by converting 2 to ² etc.
    const prettifiedUnit = formattingUnit?.replace(/2/g, '²').replace(/3/g, '³')

    const formatOptions: Intl.NumberFormatOptions = {
      maximumFractionDigits: decimals ?? maximumFractionDigits.value
    }

    return {
      value: formatNumber(formattingNumber, formatOptions).value,
      unit: prettifiedUnit
    }
  }

  const withUnit = (value: number, decimals?: number, unit?: string, toUnit?: string): string => {
    const { value: formattedValue, unit: prettifiedUnit } = withUnitSeparate(value, decimals, unit, toUnit)
    return `${formattedValue} ${prettifiedUnit}`
  }

  const formatNumber = (value: number, options?: Intl.NumberFormatOptions) => {
    const formatOptions: Intl.NumberFormatOptions = {
      ...options,
      maximumFractionDigits: options?.maximumFractionDigits ?? maximumFractionDigits.value,
      minimumFractionDigits: options?.minimumFractionDigits
    }

    return computed(() => {
      return Intl.NumberFormat(language.value as string[], formatOptions).format(value)
    })
  }
  return {
    formatNumber,
    withUnit,
    withUnitSeparate,
    withQuantifiedSymbol
  }
}
